import { OAuth2Token, UserToken } from '../types';
import { StorageAdapter } from '../storage/interfaces';
import { System, Scope, User } from '../types';
import { OAuth2Provider } from '../providers/base.provider';
import { TokenOptions, UserCreationOptions } from './oauth2.facade';

// Granular facade interface for explicit operations
export interface OAuth2GranularOperations {
  // User operations
  createUserInSystem(systemId: string, options: UserCreationOptions): Promise<User>;
  getOrCreateUserInSystem(systemId: string, options: UserCreationOptions): Promise<User>;
  getUserById(userId: string): Promise<User | null>;
  findUserByEmailInSystem(systemId: string, email: string): Promise<User | null>;
  findUserByExternalIdInSystem(systemId: string, externalId: string): Promise<User | null>;
  getUsersBySystem(systemId: string): Promise<User[]>;

  // Token operations - all levels of querying
  saveTokenForUser(
    userId: string,
    systemId: string,
    scopeId: string,
    provider: string,
    email: string,
    token: OAuth2Token,
  ): Promise<UserToken>;
  getTokensByUser(userId: string): Promise<UserToken[]>;
  getTokensByUserAndScope(userId: string, scopeId: string): Promise<UserToken[]>;
  getTokensByUserAndProvider(userId: string, provider: string): Promise<UserToken[]>;
  getTokensByUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken[]>;
  getTokensByScope(systemId: string, scopeId: string): Promise<UserToken[]>;
  getTokensByProvider(systemId: string, provider: string): Promise<UserToken[]>;
  getTokensBySystem(systemId: string): Promise<UserToken[]>;

  // Email-based queries
  findTokensByEmail(email: string, systemId: string): Promise<UserToken[]>;
  findTokensByEmailAndScope(email: string, systemId: string, scopeId: string): Promise<UserToken[]>;
  findTokensByEmailAndProvider(
    email: string,
    systemId: string,
    provider: string,
  ): Promise<UserToken[]>;
  findTokenByEmailScopeProvider(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken | null>;

  // Token validation and refresh - Email-specific (unambiguous)
  getValidTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    options?: TokenOptions,
  ): Promise<OAuth2Token>;
  getAccessTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    options?: TokenOptions,
  ): Promise<string>;
  withValidTokenByEmail<T>(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    callback: (accessToken: string) => Promise<T>,
    options?: TokenOptions,
  ): Promise<T>;

  // Token validation and refresh - Bulk operations (return all valid tokens)
  getAllValidTokensForUser(
    userId: string,
    options?: TokenOptions,
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]>;
  getAllValidTokensForUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    options?: TokenOptions,
  ): Promise<{ email: string; token: OAuth2Token; userToken: UserToken }[]>;
  getAllValidTokensForEmail(
    email: string,
    systemId: string,
    options?: TokenOptions,
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]>;

  // Token existence checks
  hasTokensForUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<boolean>;
  hasTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<boolean>;
  hasTokenWithEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
  ): Promise<boolean>;

  // Email validation within provider
  replaceTokensByEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
    token: OAuth2Token,
  ): Promise<UserToken>;

  // Token management - User-based deletions
  deleteTokensByUser(userId: string): Promise<void>;
  deleteTokensByUserAndScope(userId: string, scopeId: string): Promise<void>;
  deleteTokensByUserAndProvider(userId: string, provider: string): Promise<void>;
  deleteTokensByUserScopeProvider(userId: string, scopeId: string, provider: string): Promise<void>;
  deleteTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<void>;

  // System and scope operations
  createSystem(name: string, description?: string): Promise<System>;
  getSystem(systemId: string): Promise<System | null>;
  createScopeInSystem(
    systemId: string,
    name: string,
    options?: {
      type?: 'authentication' | 'access' | 'custom';
      permissions?: string[];
      isolated?: boolean;
    },
  ): Promise<Scope>;
  getScope(scopeId: string): Promise<Scope | null>;
  getScopesBySystem(systemId: string): Promise<Scope[]>;
}

export class OAuth2GranularClient implements OAuth2GranularOperations {
  constructor(
    private storage: StorageAdapter,
    private providers: Map<string, OAuth2Provider>,
    private now: () => number = Date.now,
  ) {}

  // User operations
  async createUserInSystem(systemId: string, options: UserCreationOptions = {}): Promise<User> {
    return this.storage.createUser({
      systemId,
      metadata: {
        ...options.metadata,
        ...(options.email && { email: options.email }),
        ...(options.externalId && { externalId: options.externalId }),
      },
    });
  }

  async getOrCreateUserInSystem(
    systemId: string,
    options: UserCreationOptions = {},
  ): Promise<User> {
    return this.storage.getOrCreateUser({
      systemId,
      email: options.email,
      externalId: options.externalId,
      metadata: options.metadata,
    });
  }

  async getUserById(userId: string): Promise<User | null> {
    return this.storage.getUser(userId);
  }

  async findUserByEmailInSystem(systemId: string, email: string): Promise<User | null> {
    return this.storage.findUserByEmail(systemId, email);
  }

  async findUserByExternalIdInSystem(systemId: string, externalId: string): Promise<User | null> {
    return this.storage.findUserByExternalId(systemId, externalId);
  }

  async getUsersBySystem(systemId: string): Promise<User[]> {
    return this.storage.getUsersBySystem(systemId);
  }

  // Token operations
  async saveTokenForUser(
    userId: string,
    systemId: string,
    scopeId: string,
    provider: string,
    email: string,
    token: OAuth2Token,
  ): Promise<UserToken> {
    return this.storage.saveTokenWithEmailValidation(userId, systemId, scopeId, provider, email, {
      userId,
      systemId,
      scopeId,
      provider,
      token,
    });
  }

  async getTokensByUser(userId: string): Promise<UserToken[]> {
    return this.storage.getTokensByUser(userId);
  }

  async getTokensByUserAndScope(userId: string, scopeId: string): Promise<UserToken[]> {
    return this.storage.getTokensByUserAndScope(userId, scopeId);
  }

  async getTokensByUserAndProvider(userId: string, provider: string): Promise<UserToken[]> {
    return this.storage.getTokensByUserAndProvider(userId, provider);
  }

  async getTokensByUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken[]> {
    return this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
  }

  async getTokensByScope(systemId: string, scopeId: string): Promise<UserToken[]> {
    return this.storage.getTokensByScope(systemId, scopeId);
  }

  async getTokensByProvider(systemId: string, provider: string): Promise<UserToken[]> {
    return this.storage.getTokensByProvider(systemId, provider);
  }

  async getTokensBySystem(systemId: string): Promise<UserToken[]> {
    return this.storage.getTokensBySystem(systemId);
  }

  // Email-based queries
  async findTokensByEmail(email: string, systemId: string): Promise<UserToken[]> {
    return this.storage.findTokensByEmail(email, systemId);
  }

  async findTokensByEmailAndScope(
    email: string,
    systemId: string,
    scopeId: string,
  ): Promise<UserToken[]> {
    return this.storage.findTokensByEmailAndScope(email, systemId, scopeId);
  }

  async findTokensByEmailAndProvider(
    email: string,
    systemId: string,
    provider: string,
  ): Promise<UserToken[]> {
    return this.storage.findTokensByEmailAndProvider(email, systemId, provider);
  }

  async findTokenByEmailScopeProvider(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<UserToken | null> {
    return this.storage.findTokenByEmailScopeProvider(email, systemId, scopeId, provider);
  }

  // Token validation and refresh - Email-specific (unambiguous)
  async getValidTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    options: TokenOptions = {},
  ): Promise<OAuth2Token> {
    const userToken = await this.storage.findTokenByEmailScopeProvider(
      email,
      systemId,
      scopeId,
      provider,
    );
    if (!userToken) {
      throw new Error(`No token found for email ${email} and provider ${provider}`);
    }

    // Check if token needs refresh
    const needsRefresh =
      options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

    if (!needsRefresh) {
      return userToken.token;
    }

    // Refresh the token
    if (!userToken.token.refreshToken) {
      throw new Error('Token expired and no refresh token available');
    }

    const providerInstance = this.providers.get(provider);
    if (!providerInstance) throw new Error(`Provider ${provider} not found`);

    const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
    await this.storage.updateToken(userToken.id, { token: newToken });

    return newToken;
  }

  async getAccessTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    options: TokenOptions = {},
  ): Promise<string> {
    const token = await this.getValidTokenByEmail(email, systemId, scopeId, provider, options);
    return token.accessToken;
  }

  // Token validation and refresh - Bulk operations (return all valid tokens)
  async getAllValidTokensForUser(
    userId: string,
    options: TokenOptions = {},
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]> {
    const userTokens = await this.storage.getTokensByUser(userId);
    const validTokens: {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  async getAllValidTokensForUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    options: TokenOptions = {},
  ): Promise<{ email: string; token: OAuth2Token; userToken: UserToken }[]> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    const validTokens: { email: string; token: OAuth2Token; userToken: UserToken }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Get user to extract email from metadata
        const user = await this.storage.getUser(userToken.userId);
        const email = user?.metadata?.email as string;

        if (!email) {
          console.warn(`No email found for user ${userToken.userId}, skipping token`);
          continue;
        }

        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        validTokens.push({
          email,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  async getAllValidTokensForEmail(
    email: string,
    systemId: string,
    options: TokenOptions = {},
  ): Promise<{ provider: string; scopeId: string; token: OAuth2Token; userToken: UserToken }[]> {
    const userTokens = await this.storage.findTokensByEmail(email, systemId);
    const validTokens: {
      provider: string;
      scopeId: string;
      token: OAuth2Token;
      userToken: UserToken;
    }[] = [];

    for (const userToken of userTokens) {
      if (!userToken) continue;

      try {
        // Check if token needs refresh
        const needsRefresh =
          options.autoRefresh !== false && this.isTokenExpired(userToken.token, options);

        let finalToken = userToken.token;

        if (needsRefresh) {
          // Try to refresh the token
          if (!userToken.token.refreshToken) {
            console.warn(
              `Token for provider ${userToken.provider} expired and no refresh token available`,
            );
            continue; // Skip this token
          }

          const providerInstance = this.providers.get(userToken.provider);
          if (!providerInstance) {
            console.warn(`Provider ${userToken.provider} not found`);
            continue; // Skip this token
          }

          try {
            const newToken = await providerInstance.refreshToken(userToken.token.refreshToken);
            await this.storage.updateToken(userToken.id, { token: newToken });
            finalToken = newToken;
          } catch (refreshError) {
            console.warn(
              `Failed to refresh token for provider ${userToken.provider}:`,
              refreshError,
            );
            continue; // Skip this token
          }
        }

        validTokens.push({
          provider: userToken.provider,
          scopeId: userToken.scopeId,
          token: finalToken,
          userToken: { ...userToken, token: finalToken },
        });
      } catch (error) {
        console.warn(`Error processing token for provider ${userToken.provider}:`, error);
        continue; // Skip this token
      }
    }

    return validTokens;
  }

  async withValidTokenByEmail<T>(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
    callback: (accessToken: string) => Promise<T>,
    options: TokenOptions = {},
  ): Promise<T> {
    const token = await this.getValidTokenByEmail(email, systemId, scopeId, provider, options);
    return await callback(token.accessToken);
  }

  // Token existence checks
  async hasTokensForUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<boolean> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    return userTokens.length > 0;
  }

  async hasTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<boolean> {
    const userToken = await this.storage.findTokenByEmailScopeProvider(
      email,
      systemId,
      scopeId,
      provider,
    );
    return userToken !== null;
  }

  async hasTokenWithEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
  ): Promise<boolean> {
    return this.storage.hasTokenWithEmailInUserScopeProvider(userId, scopeId, provider, email);
  }

  // Email validation within provider
  async replaceTokensByEmailInUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
    email: string,
    token: OAuth2Token,
  ): Promise<UserToken> {
    return this.storage.replaceTokensByEmailInUserScopeProvider(userId, scopeId, provider, email, {
      userId,
      systemId: (await this.storage.getUser(userId))?.systemId || '',
      scopeId,
      provider,
      token,
    });
  }

  // Token management - User-based deletions
  async deleteTokensByUser(userId: string): Promise<void> {
    return this.storage.deleteTokensByUser(userId);
  }

  async deleteTokensByUserAndScope(userId: string, scopeId: string): Promise<void> {
    return this.storage.deleteTokensByUserAndScope(userId, scopeId);
  }

  async deleteTokensByUserAndProvider(userId: string, provider: string): Promise<void> {
    return this.storage.deleteTokensByUserAndProvider(userId, provider);
  }

  async deleteTokensByUserScopeProvider(
    userId: string,
    scopeId: string,
    provider: string,
  ): Promise<void> {
    const userTokens = await this.storage.getTokensByUserScopeProvider(userId, scopeId, provider);
    for (const userToken of userTokens) {
      await this.storage.deleteToken(userToken.id);
    }
  }

  async deleteTokenByEmail(
    email: string,
    systemId: string,
    scopeId: string,
    provider: string,
  ): Promise<void> {
    const userToken = await this.storage.findTokenByEmailScopeProvider(
      email,
      systemId,
      scopeId,
      provider,
    );
    if (userToken) {
      await this.storage.deleteToken(userToken.id);
    }
  }

  // System and scope operations
  async createSystem(name: string, description?: string): Promise<System> {
    return this.storage.createSystem({ name, description, scopes: [] });
  }

  async getSystem(systemId: string): Promise<System | null> {
    return this.storage.getSystem(systemId);
  }

  async createScopeInSystem(
    systemId: string,
    name: string,
    options: {
      type?: 'authentication' | 'access' | 'custom';
      permissions?: string[];
      isolated?: boolean;
    } = {},
  ): Promise<Scope> {
    return this.storage.createScope({
      systemId,
      name,
      type: options.type || 'authentication',
      permissions: options.permissions || [],
      isolated: options.isolated ?? true,
    });
  }

  async getScope(scopeId: string): Promise<Scope | null> {
    return this.storage.getScope(scopeId);
  }

  async getScopesBySystem(systemId: string): Promise<Scope[]> {
    return this.storage.getScopesBySystem(systemId);
  }

  // Helper methods
  private isTokenExpired(token: OAuth2Token, options: TokenOptions = {}): boolean {
    const { expirationBuffer = 300 } = options;

    // Use createdAt if available, otherwise use expiresAt
    if (token.createdAt && token.expiresIn !== undefined) {
      const expiresAt = token.createdAt + token.expiresIn * 1000;
      const effectiveNow = this.now() + expirationBuffer * 1000;
      return effectiveNow >= expiresAt;
    }

    // Fallback to expiresAt date
    const expiresAt = new Date(token.expiresAt).getTime();
    const effectiveNow = this.now() + expirationBuffer * 1000;
    return effectiveNow >= expiresAt;
  }
}
